import React from 'react';
import { BookOpenIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { CtaBand } from '../components/CtaBand';
import { contactDetails, credentials, portraitUrl } from '../data/content';
import { career, publications } from '../data/experience';
export function About() {
  return <>
      <PageHeader label="About Dr. Tom Vecchione" title="Grounded in Behavioral Science. Built on Decades of Executive Leadership." intro="A rare combination of doctoral-level academic rigor, senior university administration, and nearly two decades leading a premier executive career practice." data-id="element-160" data-mp="2eji0bl9hhxzo" />

      {/* Biography */}
      <section className="bg-white" aria-labelledby="bio-heading" data-id="element-161" data-mp="2x97vi9h5sls4">
        <div className="mx-auto grid max-w-7xl items-start gap-14 px-6 py-24 lg:grid-cols-12 lg:px-8 lg:py-28" data-id="element-162" data-mp="2x7d9eru5inmg">
          <div className="lg:col-span-5" data-id="element-163" data-mp="1gxawidl089od">
            <div className="border border-hairline bg-white p-3 lg:sticky lg:top-28" data-id="element-164" data-mp="1luazugqgyybs">
              <img src={portraitUrl} alt="Dr. Tom Vecchione" className="aspect-square w-full object-cover object-top" data-id="element-165" data-mp="1uha1bljz9ctx" />
            </div>
          </div>
          <div className="lg:col-span-7" data-id="element-166" data-mp="23iq8lkfhwytx">
            <h2 id="bio-heading" className="font-serif text-3xl leading-tight text-navy sm:text-4xl" data-id="element-167" data-mp="23gdigjzl3i5s">
              Where academic rigor meets executive judgment.
            </h2>
            <div className="mt-8 space-y-6 text-lg leading-relaxed text-charcoal" data-id="element-168" data-mp="2df350q2fdqsd">
              <p data-id="element-169" data-mp="28ghzcb4emewj">
                Dr. Tom Vecchione holds a Ph.D. in Counselor Education — doctoral training in human development,
                career theory, and behavioral assessment that informs how he uncovers, frames, and communicates
                leadership value.
              </p>
              <p data-id="element-170" data-mp="2w86yaabqe1mg">
                Before founding his advisory practice, he served as Associate Vice President and Executive Director
                at leading institutions including Kennesaw State University, the University of the Pacific, and Elon
                University — building career strategy at an institutional scale and partnering with employers across
                industries.
              </p>
              <p data-id="element-171" data-mp="1xtrp139v5jzc">
                For more than 17 years he has led <span className="font-semibold text-navy" data-id="element-172" data-mp="22lrkojiwhqq4">Write for Me Resumes</span>,
                guiding over 1,150 mid-level and executive clients through resume engineering, interview coaching, and
                complex career transitions.
              </p>
            </div>
            <dl className="mt-12 grid gap-6 border-t border-hairline pt-8 sm:grid-cols-3" data-id="element-173" data-mp="2cm1urv9n2i12">
              <div data-id="element-174" data-mp="2gjzn36yphk8o">
                <dt className="text-sm text-steel" data-id="element-175" data-mp="2lkndprj0vz2d">Based in</dt>
                <dd className="mt-1 font-semibold text-navy" data-id="element-176" data-mp="2lk42sursiaxu">{contactDetails.location}</dd>
              </div>
              <div data-id="element-177" data-mp="m9vx8ew0s8er">
                <dt className="text-sm text-steel" data-id="element-178" data-mp="r6mf97rdxe2t">Practice</dt>
                <dd className="mt-1 font-semibold text-navy" data-id="element-179" data-mp="r74jaqkagwla">Write for Me Resumes, 17+ years</dd>
              </div>
              <div data-id="element-180" data-mp="jp6ev0gysbox">
                <dt className="text-sm text-steel" data-id="element-181" data-mp="ewwq3nrl5ho8">Clients served</dt>
                <dd className="mt-1 font-semibold text-navy" data-id="element-182" data-mp="euk14qneu71o">1,150+ professionals and executives</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="bg-navy" aria-labelledby="credentials-heading" data-id="element-183" data-mp="4wmc40xq8680">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-28" data-id="element-184" data-mp="12lpi595375dk">
          <h2 id="credentials-heading" className="max-w-2xl font-serif text-3xl leading-tight text-white sm:text-4xl" data-id="element-185" data-mp="xo61y3otwiiz">
            Doctoral training. Triple-certified expertise.
          </h2>
          <dl className="mt-14 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-186" data-mp="on7utcj3fuw6">
            {credentials.map(c => <div key={c.abbr} className="flex flex-col bg-navy p-8 lg:pr-10" data-id="element-187" data-mp="3dftwxtka80z5">
                <dt data-id="element-188" data-mp="39bm9dzpk8r2i">
                  <span className="block font-serif text-4xl text-gold" data-id="element-189" data-mp="39bds7vmemzli">{c.abbr}</span>
                  <span className="mt-4 block font-semibold leading-snug text-white" data-id="element-190" data-mp="1137dq2c6qarc">{c.name}</span>
                </dt>
                <dd className="mt-3 text-sm leading-relaxed text-white/70" data-id="element-191" data-mp="15wschwllr8rl">{c.description}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      {/* Leadership record */}
      <section className="bg-white" aria-labelledby="career-heading" data-id="element-192" data-mp="w2tf1awh4d5z">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 lg:grid-cols-12 lg:px-8 lg:py-28" data-id="element-193" data-mp="1jvs0d21ajjmp">
          <div className="lg:col-span-4" data-id="element-194" data-mp="1os8ldnd71hvk">
            <h2 id="career-heading" className="font-serif text-3xl leading-tight text-navy sm:text-4xl" data-id="element-195" data-mp="1atsjqau6uhka">
              Leadership record.
            </h2>
            <p className="mt-5 leading-relaxed text-steel" data-id="element-196" data-mp="1eu2qvqtpve0q">
              Over 25 years leading career strategy across private practice and higher education.
            </p>
          </div>
          <ol className="relative border-l border-hairline lg:col-span-8" data-id="element-197" data-mp="3ha1unsb93od">
            {career.map(c => <li key={c.organization} className="relative pb-12 pl-10 last:pb-0" data-id="element-198" data-mp="3s0irnxayycy1">
                <span className="absolute -left-[5px] top-2 h-[9px] w-[9px] rotate-45 bg-gold" aria-hidden="true" data-id="element-199" data-mp="3n1z63y0af367" />
                <p className="text-sm font-medium text-steel" data-id="element-200" data-mp="5udhvg8bgwo9">{c.period}</p>
                <h3 className="mt-1 font-serif text-2xl text-navy" data-id="element-201" data-mp="vix48qsgy1i">{c.organization}</h3>
                <p className="mt-1 font-semibold text-charcoal" data-id="element-202" data-mp="39b2muxs93ybt">{c.role}</p>
                <p className="mt-3 max-w-2xl leading-relaxed text-steel" data-id="element-203" data-mp="34cj4f7cv94o7">{c.description}</p>
              </li>)}
          </ol>
        </div>
      </section>

      {/* Publications */}
      <section className="border-t border-hairline bg-ivory" aria-labelledby="pubs-heading" data-id="element-204" data-mp="3eap37g3dluou">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-28" data-id="element-205" data-mp="3e8uerrswbkg0">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-206" data-mp="2qjjkmt61woau">
            <div className="max-w-2xl" data-id="element-207" data-mp="2lli5ukfnrb56">
              <h2 id="pubs-heading" className="font-serif text-3xl leading-tight text-navy sm:text-4xl" data-id="element-208" data-mp="3ba5ew6t2qrn6">
                Publications &amp; research.
              </h2>
              <p className="mt-5 leading-relaxed text-steel" data-id="element-209" data-mp="3f9xqythoffxa">
                Published contributions that reinforce the academic foundation behind every client engagement.
              </p>
            </div>
            <p className="text-sm text-steel" data-id="element-210" data-mp="3p3ob5m6rt476">Full citations available upon request.</p>
          </div>
          <div className="mt-14 grid gap-x-10 md:grid-cols-3" data-id="element-211" data-mp="2sde0obse32pz">
            {publications.map(p => <article key={p.area} className="flex flex-col border-t-2 border-navy py-8" data-id="element-212" data-mp="2weps8mutpy0t">
                <p className="flex items-center gap-2 text-sm font-semibold text-gold-dark" data-id="element-213" data-mp="31e2f37lp353e">
                  <BookOpenIcon className="h-4 w-4" aria-hidden="true" data-id="element-214" data-mp="36cdkn64yitft" />
                  {p.area}
                </p>
                <h3 className="mt-4 font-serif text-xl leading-snug text-navy" data-id="element-215" data-mp="go4ox74iom1m">{p.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-steel" data-id="element-216" data-mp="lmo9b33gpprv">{p.description}</p>
              </article>)}
          </div>
        </div>
      </section>

      <CtaBand data-id="element-217" data-mp="214b0a6ozssxr" />
    </>;
}